// HUD signature fields (HUD representative, wage report employee/interviewer). Kept apart from
// signature.js, which the other forms still use.
// Saved signatures are shown as a plain image; the canvas is only drawn on while editing.
// The hidden field is only replaced when the user actually drew something, so resizing, a failed
// image load or a plain re-save can never overwrite a stored signature with a blank one.
// Removing a signature is explicit: it enables the .hud-signature-remove field.
// Tracked per element rather than in an attribute: Turbolinks restores cached pages as cloned DOM
// without the listeners, and those clones must be initialised again.
const initialisedSignatures = new WeakSet();

function initHudSignature(wrapper) {
    if (initialisedSignatures.has(wrapper)) return;
    initialisedSignatures.add(wrapper);

    const $ = (selector) => wrapper.querySelector(selector);
    const canvas = $('.hud-signature-canvas');
    const hidden = $('.hud-signature-hidden');
    const removeField = $('.hud-signature-remove');
    const preview = $('.signature-preview');
    const previewImage = $('.signature-preview-image');
    const editor = $('.signature-editor');
    const status = $('.signature-status');
    const form = canvas.closest('form');
    const original = hidden.value;
    const hasSaved = wrapper.dataset.hasSaved === 'true';

    let pad = null;
    let editing = !hasSaved;
    let dirty = false;      // the user drew something since the editor was opened
    let pending = null;     // new signature accepted with Done but not saved yet
    let removed = false;

    function ensurePad() {
        if (pad) return;
        // Size once, when the editor is first shown. Never resized afterwards, as resizing clears a canvas.
        const available = editor.clientWidth;
        if (available > 0) canvas.width = Math.min(650, available);
        pad = new SignaturePad(canvas, { onBegin: function () { dirty = true; removed = false; render(); } });
    }

    function drawSignature(src) {
        pad.clear();
        dirty = false;
        if (!src) return;
        const image = new Image();
        image.onload = function () {
            if (dirty) return; // the user already started drawing
            const scale = Math.min(canvas.width / image.width, canvas.height / image.height, 1);
            const w = image.width * scale, h = image.height * scale;
            canvas.getContext('2d').drawImage(image, (canvas.width - w) / 2, (canvas.height - h) / 2, w, h);
        };
        image.src = src;
    }

    function drawnDataUrl() {
        const bounds = signatureBounds(canvas);
        if (!bounds) return null;
        const trimmed = document.createElement('canvas');
        trimmed.width = bounds.width;
        trimmed.height = bounds.height;
        trimmed.getContext('2d').drawImage(canvas, bounds.left, bounds.top, bounds.width, bounds.height, 0, 0, bounds.width, bounds.height);
        return trimmed.toDataURL();
    }

    // Accept what is on the canvas, but only if the user drew something on it.
    function commit() {
        if (!pad || !dirty) return;
        const dataUrl = drawnDataUrl();
        if (!dataUrl) return;
        pending = dataUrl;
        removed = false;
        hidden.value = dataUrl;
        removeField.disabled = true;
        dirty = false;
    }

    function render() {
        const showEditor = editing || removed;
        if (preview) preview.style.display = showEditor ? 'none' : '';
        editor.style.display = showEditor ? '' : 'none';
        if (previewImage) previewImage.src = pending || original;
        removeField.disabled = !removed;
        const show = (selector, visible) => { const el = $(selector); if (el) el.style.display = visible ? '' : 'none'; };
        show('.done-signature', !removed);
        show('.remove-signature', !removed);
        if (removed) {
            status.textContent = 'The signature will be removed when you save. Draw a new one to replace it, or cancel.';
            status.className = 'signature-status small mt-1 text-danger';
        } else if (pending) {
            status.textContent = 'New signature, not saved yet. Save the form to keep it.';
            status.className = 'signature-status small mt-1 text-muted';
        }
        status.style.display = (removed || pending) ? '' : 'none';
    }

    function openEditor() {
        editing = true;
        render();
        ensurePad();
        drawSignature(pending || original);
    }

    if (!hasSaved) ensurePad();

    const on = (selector, handler) => {
        const el = $(selector);
        if (el) el.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); handler(); });
    };

    on('.edit-signature', openEditor);

    on('.clear-signature', function () { pad.clear(); dirty = false; });

    on('.done-signature', function () {
        commit();
        editing = false;
        render();
    });

    on('.remove-signature', function () {
        if (!window.confirm(wrapper.dataset.confirmRemove)) return;
        removed = true;
        pending = null;
        hidden.value = '';
        pad.clear();
        dirty = false;
        render();
    });

    on('.cancel-edit-signature', function () {
        // Discard everything done in this editing session, including a pending removal
        pending = null;
        removed = false;
        dirty = false;
        hidden.value = original;
        editing = false;
        render();
    });

    form.addEventListener('submit', commit);
    render();
}

// Bounding box of the non-transparent pixels, padded; null when the canvas is blank.
function signatureBounds(canvas) {
    const padding = 10;
    const width = canvas.width, height = canvas.height;
    const data = canvas.getContext('2d').getImageData(0, 0, width, height).data;
    let top = null, left = null, right = null, bottom = null;

    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            if (data[(y * width + x) * 4 + 3] === 0) continue;
            if (top === null) top = y;
            if (left === null || x < left) left = x;
            if (right === null || x > right) right = x;
            bottom = y;
        }
    }
    if (top === null) return null;

    left = Math.max(0, left - padding);
    top = Math.max(0, top - padding);
    right = Math.min(width - 1, right + padding);
    bottom = Math.min(height - 1, bottom + padding);
    return { left: left, top: top, width: right - left + 1, height: bottom - top + 1 };
}

document.addEventListener('turbolinks:load', function () {
    document.querySelectorAll('.hud-signature').forEach(initHudSignature);
});
